import { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Typography,
    keyframes,
} from '@mui/material';
import { SCUIButton } from '@/atomic/atoms/SCUIButton/SCUIButton';
import { useTranslation } from 'react-i18next';
import { SCUIBreadCrumb } from '@/atomic/molecules/SCUIBreadCrumb/SCUIBreadCrumb';
import { SCUIContent } from '@/atomic/organisms/SCUIContent/SCUIContent';
import { SCUILoadingLinear } from '@/atomic/atoms/SCUILoadingLinear/SCUILoadingLinear';
import { PageUrl } from '@/types/enums/pageUrl-enums';
import { GWD } from './growWorkingDays.classes';
import CheckIcon from '@mui/icons-material/Check';
import SaveIcon from '@mui/icons-material/Save';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Swal from 'sweetalert2';
import { OFSLogsCommand } from '@/services/command';

// ─── Day metadata ─────────────────────────────────────────────────────────────

interface DayMeta {
    /** JS dayOfWeek: 0=Sun, 1=Mon … 6=Sat */
    dayOfWeek: number;
    /** Backend integer: 1=Mon … 7=Sun */
    backendInt: number;
    translationKey: string;
    shortKey: string;
    isWeekend: boolean;
}

const DAYS_ORDER: DayMeta[] = [
    { dayOfWeek: 1, backendInt: 1, translationKey: 'growWorkingDays.days.monday',    shortKey: 'growWorkingDays.daysShort.monday',    isWeekend: false },
    { dayOfWeek: 2, backendInt: 2, translationKey: 'growWorkingDays.days.tuesday',   shortKey: 'growWorkingDays.daysShort.tuesday',   isWeekend: false },
    { dayOfWeek: 3, backendInt: 3, translationKey: 'growWorkingDays.days.wednesday', shortKey: 'growWorkingDays.daysShort.wednesday', isWeekend: false },
    { dayOfWeek: 4, backendInt: 4, translationKey: 'growWorkingDays.days.thursday',  shortKey: 'growWorkingDays.daysShort.thursday',  isWeekend: false },
    { dayOfWeek: 5, backendInt: 5, translationKey: 'growWorkingDays.days.friday',    shortKey: 'growWorkingDays.daysShort.friday',    isWeekend: false },
    { dayOfWeek: 6, backendInt: 6, translationKey: 'growWorkingDays.days.saturday',  shortKey: 'growWorkingDays.daysShort.saturday',  isWeekend: true  },
    { dayOfWeek: 0, backendInt: 7, translationKey: 'growWorkingDays.days.sunday',    shortKey: 'growWorkingDays.daysShort.sunday',    isWeekend: true  },
];

/** Map backend int (1-7) → JS dayOfWeek (0=Sun, 1=Mon … 6=Sat) */
const backendIntToJsDayOfWeek = (backendInt: number): number => {
    const found = DAYS_ORDER.find((d) => d.backendInt === backendInt);
    return found?.dayOfWeek ?? -1;
};

/** Map JS dayOfWeek → backend int */
const jsDayOfWeekToBackendInt = (dayOfWeek: number): number => {
    const found = DAYS_ORDER.find((d) => d.dayOfWeek === dayOfWeek);
    return found?.backendInt ?? -1;
};

// ─── Hour options 0-23 ────────────────────────────────────────────────────────

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i);

// ─── Color palette ────────────────────────────────────────────────────────────

const WORK_GREEN = {
    bg: '#c8e6c9',
    bgHover: '#a5d6a7',
    bgStrong: '#81c784',
    border: '#66bb6a',
    borderHover: '#4caf50',
    text: '#1b5e20',
    textMuted: '#2e7d32',
    shadow: 'rgba(76, 175, 80, 0.42)',
    shadowSoft: 'rgba(76, 175, 80, 0.26)',
};

const activeCountPulse = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 8px 24px ${WORK_GREEN.shadowSoft};
  }
  40% {
    transform: scale(1.035);
    box-shadow: 0 12px 32px ${WORK_GREEN.shadow};
  }
  100% {
    transform: scale(1);
    box-shadow: 0 8px 24px ${WORK_GREEN.shadowSoft};
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────

export const GrowWorkingDays = () => {
    const { t } = useTranslation();

    const selectedCountry = JSON.parse(localStorage.getItem('selectedCountry') || '{}');
    const countryId: number = selectedCountry?.countryId ?? 0;
    const countryCode: string = selectedCountry?.countryCode ?? '';

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [selectedDays, setSelectedDays] = useState<Set<number>>(new Set());
    const [defaultSelectedDays, setDefaultSelectedDays] = useState<Set<number>>(new Set());
    const [startHour, setStartHour] = useState<number>(9);
    const [endHour, setEndHour] = useState<number>(18);
    const [defaultStartHour, setDefaultStartHour] = useState<number>(9);
    const [defaultEndHour, setDefaultEndHour] = useState<number>(18);
    const [activeCountAnimating, setActiveCountAnimating] = useState(false);

    const isDefaultSelection = useMemo(() => {
        if (selectedDays.size !== defaultSelectedDays.size) return false;
        if (startHour !== defaultStartHour || endHour !== defaultEndHour) return false;
        for (const dayOfWeek of selectedDays) {
            if (!defaultSelectedDays.has(dayOfWeek)) return false;
        }
        return true;
    }, [selectedDays, defaultSelectedDays, startHour, endHour, defaultStartHour, defaultEndHour]);

    // ── Load ────────────────────────────────────────────────────────────────

    const loadWorkingDays = async () => {
        if (!countryId) return;
        setLoading(true);
        try {
            const res = await OFSLogsCommand.PostGetHolidayDatesForCountry({ countryId });
            const data = res?.data?.country;
            if (data) {
                // Backend holidays = days NOT working; selectedDays = working days
                const holidayJsDays = new Set<number>(
                    (data.holidays ?? []).map((h: number) => backendIntToJsDayOfWeek(h)).filter((d: number) => d !== -1),
                );
                const workingSet = new Set<number>(
                    DAYS_ORDER.map((d) => d.dayOfWeek).filter((d) => !holidayJsDays.has(d)),
                );
                setDefaultSelectedDays(workingSet);
                setSelectedDays(workingSet);

                const sHour = data.startHour ?? 9;
                const eHour = data.endHour ?? 18;
                setStartHour(sHour);
                setEndHour(eHour);
                setDefaultStartHour(sHour);
                setDefaultEndHour(eHour);
            }
        } catch {
            Swal.fire(t('growWorkingDays.loadError'), '', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadWorkingDays();
    }, []);

    // ── Toggle ──────────────────────────────────────────────────────────────

    const toggleDay = (dayOfWeek: number) => {
        setSelectedDays((prev) => {
            const next = new Set(prev);
            if (next.has(dayOfWeek)) {
                next.delete(dayOfWeek);
            } else {
                next.add(dayOfWeek);
            }
            return next;
        });
        setActiveCountAnimating(true);
    };

    useEffect(() => {
        if (!activeCountAnimating) return;
        const timer = window.setTimeout(() => setActiveCountAnimating(false), 480);
        return () => window.clearTimeout(timer);
    }, [activeCountAnimating]);

    const handleResetToDefault = () => {
        setSelectedDays(new Set(defaultSelectedDays));
        setStartHour(defaultStartHour);
        setEndHour(defaultEndHour);
        setActiveCountAnimating(true);
    };

    // ── Validate ────────────────────────────────────────────────────────────

    const validate = (): boolean => {
        if (!countryId) {
            Swal.fire(t('general.error'), t('growWorkingDays.noCountryError'), 'error');
            return false;
        }
        if (startHour >= endHour) {
            Swal.fire(t('general.error'), t('growWorkingDays.hourRangeError'), 'error');
            return false;
        }
        return true;
    };

    // ── Save ────────────────────────────────────────────────────────────────

    const handleSave = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            // holidays = days NOT in selectedDays, converted to backend int
            const holidays = DAYS_ORDER.filter((d) => !selectedDays.has(d.dayOfWeek)).map((d) =>
                jsDayOfWeekToBackendInt(d.dayOfWeek),
            );

            const res = await OFSLogsCommand.PostSetHolidayDatesForCountries({
                countries: [
                    {
                        countryId,
                        countryCode,
                        startHour,
                        endHour,
                        holidays,
                    },
                ],
            });

            const isSuccess = res?.result ?? false;
            if (isSuccess) {
                // Update defaults to reflect the saved state
                setDefaultSelectedDays(new Set(selectedDays));
                setDefaultStartHour(startHour);
                setDefaultEndHour(endHour);
                Swal.fire({
                    title: t('general.success'),
                    text: t('growWorkingDays.saveSuccess'),
                    icon: 'success',
                    timer: 2500,
                    showConfirmButton: false,
                });
            } else {
                Swal.fire(t('general.error'), t('growWorkingDays.saveError'), 'error');
            }
        } catch {
            Swal.fire(t('general.error'), t('growWorkingDays.saveError'), 'error');
        } finally {
            setSaving(false);
        }
    };

    // ── Render ──────────────────────────────────────────────────────────────

    const activeCount = selectedDays.size;

    return (
        <SCUIContent className={GWD.page}>
            {/* Breadcrumb */}
            <Box className={GWD.breadcrumbSection}>
                <SCUIBreadCrumb title={t('growWorkingDays.title')} componentLink={PageUrl.growWorkingDays.url} />
            </Box>

            {/* Loading bar */}
            <Box className={GWD.loadingSection}>
                <SCUILoadingLinear display={loading} />
            </Box>

            {/* Main content */}
            <Box className={GWD.contentSection}>
                {/* Header card */}
                <Card
                    elevation={0}
                    sx={{
                        mb: 3,
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                    }}
                >
                    <CardContent sx={{ py: '16px !important' }}>
                        {/* Title row */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Box
                                sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: 2,
                                    bgcolor: 'primary.main',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <CalendarTodayIcon sx={{ color: 'white', fontSize: 22 }} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                                    {t('growWorkingDays.subTitle')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" mt={0.4}>
                                    {t('growWorkingDays.description')}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Working hours section */}
                        <Box
                            className={GWD.workingHoursSection}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                flexWrap: 'wrap',
                                pt: 1,
                                mt: 1,
                                borderTop: '1px solid',
                                borderColor: 'divider',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}>
                                <AccessTimeIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                <Typography variant="body2" fontWeight={600} color="text.secondary">
                                    {t('growWorkingDays.workingHoursLabel')}
                                </Typography>
                            </Box>

                            <FormControl size="small" sx={{ minWidth: 130 }}>
                                <InputLabel id="start-hour-label">
                                    {t('growWorkingDays.startHourLabel')}
                                </InputLabel>
                                <Select
                                    className={GWD.startHourInput}
                                    labelId="start-hour-label"
                                    value={startHour}
                                    label={t('growWorkingDays.startHourLabel')}
                                    disabled={loading || saving}
                                    onChange={(e) => setStartHour(Number(e.target.value))}
                                >
                                    {HOUR_OPTIONS.map((h) => (
                                        <MenuItem key={h} value={h}>
                                            {String(h).padStart(2, '0')}:00
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                —
                            </Typography>

                            <FormControl size="small" sx={{ minWidth: 130 }}>
                                <InputLabel id="end-hour-label">
                                    {t('growWorkingDays.endHourLabel')}
                                </InputLabel>
                                <Select
                                    className={GWD.endHourInput}
                                    labelId="end-hour-label"
                                    value={endHour}
                                    label={t('growWorkingDays.endHourLabel')}
                                    disabled={loading || saving}
                                    onChange={(e) => setEndHour(Number(e.target.value))}
                                >
                                    {HOUR_OPTIONS.map((h) => (
                                        <MenuItem key={h} value={h}>
                                            {String(h).padStart(2, '0')}:00
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {startHour >= endHour && (
                                <Typography variant="caption" color="error" fontWeight={600}>
                                    {t('growWorkingDays.hourRangeError')}
                                </Typography>
                            )}
                        </Box>
                    </CardContent>
                </Card>

                {/* Active day count — centered circle above calendar */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <Box
                        className={GWD.activeCountBanner}
                        sx={{
                            width: { xs: 100, sm: 114, md: 126 },
                            height: { xs: 100, sm: 114, md: 126 },
                            borderRadius: '50%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.25,
                            px: 1,
                            textAlign: 'center',
                            background: (theme) =>
                                activeCount > 0
                                    ? `linear-gradient(145deg, ${WORK_GREEN.bg} 0%, ${WORK_GREEN.bgStrong} 100%)`
                                    : `linear-gradient(145deg, ${theme.palette.grey[200]} 0%, ${theme.palette.grey[300]} 100%)`,
                            border: '2px solid',
                            borderColor: activeCount > 0 ? WORK_GREEN.border : 'grey.400',
                            boxShadow: `0 8px 24px ${WORK_GREEN.shadowSoft}`,
                            animation: activeCountAnimating ? `${activeCountPulse} 0.48s ease-out` : 'none',
                        }}
                    >
                        <Typography
                            component="span"
                            sx={{
                                color: WORK_GREEN.text,
                                fontSize: '24px !important',
                                marginBottom: '8px !important',
                                fontWeight: 800,
                                lineHeight: 1,
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {activeCount}
                        </Typography>
                        <Typography
                            sx={{
                                color: WORK_GREEN.textMuted,
                                fontSize: { xs: '0.72rem', sm: '0.78rem', md: '0.85rem' },
                                fontWeight: 700,
                                lineHeight: 1.15,
                                maxWidth: '92%',
                            }}
                        >
                            {t('growWorkingDays.activeDaysLabel')}
                        </Typography>
                    </Box>
                </Box>

                {/* Day grid */}
                <Box
                    className={GWD.calendarGrid}
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: 2,
                        mb: 3,
                    }}
                >
                    {DAYS_ORDER.map((day) => {
                        const isSelected = selectedDays.has(day.dayOfWeek);
                        const isWeekend = day.isWeekend;

                        return (
                            <Box
                                key={day.dayOfWeek}
                                className={`${GWD.dayCard} ${isSelected ? GWD.dayCardSelected : ''}`}
                                onClick={() => !loading && toggleDay(day.dayOfWeek)}
                                sx={{
                                    position: 'relative',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1.5,
                                    minHeight: 220,
                                    pt: isWeekend ? 4.5 : 0,
                                    borderRadius: 3,
                                    border: '2px solid',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    userSelect: 'none',
                                    transition: 'all 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
                                    overflow: 'hidden',

                                    borderColor: isSelected ? WORK_GREEN.border : isWeekend ? 'error.main' : 'divider',
                                    bgcolor: isSelected
                                        ? WORK_GREEN.bg
                                        : isWeekend
                                          ? 'rgba(211, 47, 47, 0.12)'
                                          : 'background.paper',

                                    boxShadow: isSelected
                                        ? `0 8px 28px ${WORK_GREEN.shadowSoft}, 0 2px 8px ${WORK_GREEN.shadowSoft}`
                                        : '0 2px 8px rgba(0,0,0,0.07)',

                                    '&:hover': loading
                                        ? {}
                                        : {
                                              borderColor: isSelected ? WORK_GREEN.borderHover : WORK_GREEN.border,
                                              boxShadow: isSelected
                                                  ? `0 12px 36px ${WORK_GREEN.shadow}, 0 4px 12px ${WORK_GREEN.shadowSoft}`
                                                  : `0 6px 18px ${WORK_GREEN.shadowSoft}`,
                                              transform: 'translateY(-4px) scale(1.012)',
                                              bgcolor: isSelected
                                                  ? WORK_GREEN.bgHover
                                                  : isWeekend
                                                    ? 'rgba(211, 47, 47, 0.18)'
                                                    : WORK_GREEN.bg,
                                          },

                                    '&:active': loading
                                        ? {}
                                        : {
                                              transform: 'translateY(-1px) scale(1.005)',
                                          },
                                }}
                            >
                                {/* Weekend header */}
                                {isWeekend && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            py: 0.85,
                                            px: 1,
                                            bgcolor: isSelected ? WORK_GREEN.bgStrong : 'error.main',
                                            borderBottom: '1px solid',
                                            borderColor: isSelected ? WORK_GREEN.border : 'error.dark',
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            component="p"
                                            textAlign="center"
                                            sx={{
                                                color: isSelected ? WORK_GREEN.text : '#fff',
                                                fontWeight: 800,
                                                letterSpacing: 1.2,
                                                textTransform: 'uppercase',
                                                fontSize: '0.72rem',
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            {t('growWorkingDays.weekendLabel')}
                                        </Typography>
                                    </Box>
                                )}

                                {/* Selected check-mark badge */}
                                {isSelected && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 12,
                                            right: 12,
                                            width: 28,
                                            height: 28,
                                            borderRadius: '50%',
                                            bgcolor: 'rgba(255,255,255,0.85)',
                                            border: '1px solid',
                                            borderColor: WORK_GREEN.border,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <CheckIcon sx={{ color: WORK_GREEN.text, fontSize: 17 }} />
                                    </Box>
                                )}

                                {/* Short name */}
                                <Typography
                                    variant="overline"
                                    fontWeight={700}
                                    letterSpacing={2}
                                    sx={{
                                        color: isSelected
                                            ? WORK_GREEN.textMuted
                                            : isWeekend
                                              ? 'error.main'
                                              : 'text.secondary',
                                        fontSize: '0.7rem',
                                        lineHeight: 1,
                                    }}
                                >
                                    {t(day.shortKey)}
                                </Typography>

                                {/* Full name */}
                                <Typography
                                    variant="h5"
                                    fontWeight={800}
                                    textAlign="center"
                                    sx={{
                                        color: isSelected ? WORK_GREEN.text : isWeekend ? 'error.dark' : 'text.primary',
                                        lineHeight: 1.1,
                                        px: 1,
                                    }}
                                >
                                    {t(day.translationKey)}
                                </Typography>

                                {/* Status badge */}
                                <Box
                                    sx={{
                                        px: 1.5,
                                        py: 0.4,
                                        borderRadius: 5,
                                        bgcolor: isSelected
                                            ? WORK_GREEN.bgStrong
                                            : isWeekend
                                              ? 'error.100'
                                              : 'action.hover',
                                        border: '1px solid',
                                        borderColor: isSelected
                                            ? WORK_GREEN.borderHover
                                            : isWeekend
                                              ? 'error.300'
                                              : 'divider',
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        fontWeight={600}
                                        sx={{
                                            color: isSelected
                                                ? WORK_GREEN.text
                                                : isWeekend
                                                  ? 'error.dark'
                                                  : 'text.secondary',
                                        }}
                                    >
                                        {isSelected
                                            ? t('growWorkingDays.statusWorking')
                                            : t('growWorkingDays.statusOff')}
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>

                {/* Footer actions */}
                <Box
                    className={GWD.footerSection}
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 1.5,
                        pt: 1,
                    }}
                >
                    <SCUIButton
                        className={GWD.inputButtonClear}
                        text={t('growWorkingDays.resetToDefault')}
                        type="secondary"
                        sizeType="fixed"
                        size="m"
                        buttonType="button"
                        onClick={handleResetToDefault}
                        disabled={loading || saving || isDefaultSelection}
                        showStartIcon
                        startIcon={<DeleteOutlineIcon />}
                    />
                    <SCUIButton
                        className={GWD.inputButtonSave}
                        text={saving ? t('growWorkingDays.saving') : t('growWorkingDays.save')}
                        type="primary"
                        sizeType="fixed"
                        size="m"
                        buttonType="button"
                        onClick={handleSave}
                        disabled={loading || saving || startHour >= endHour}
                        showStartIcon
                        startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                    />
                </Box>
            </Box>
        </SCUIContent>
    );
};
